import { useEffect } from "react";
import { useParams, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ProgressBar } from "@/components/ProgressBar";
import { getProgram, getActiveProgramId, dayCompletion, isDayComplete, isPhaseUnlocked, isProgramVisible } from "@/data/program";
import { ArrowLeft, Moon, Check, ChevronRight, Loader2 } from "lucide-react";

export default function Phase() {
  const { phaseId } = useParams();
  const navigate = useNavigate();
  const { user, completed, progressLoaded } = useAuth();

  const program = getProgram(getActiveProgramId());

  useEffect(() => {
    if (program && !isProgramVisible(program, user?.email)) {
      navigate("/select", { replace: true });
    }
  }, [program, user, navigate]);

  const phaseIndex = program?.phases.findIndex((p) => p.id === phaseId) ?? -1;
  const phase = phaseIndex >= 0 ? program.phases[phaseIndex] : null;
  if (!phase) return null;
  if (!progressLoaded)
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="animate-spin text-[#fa003f]" size={28} />
      </div>
    );
  if (!isPhaseUnlocked(program, phaseIndex, completed))
    return <Navigate to="/program" replace />;

  return (
    <div className="animate-slideup">
      <div className="px-5 pt-12">
        <button
          data-testid="back-button"
          onClick={() => navigate("/program")}
          className="mb-4 flex items-center gap-1 text-sm uppercase tracking-wider text-zinc-500"
        >
          <ArrowLeft size={18} /> Programma
        </button>
        <span className="font-head text-[11px] uppercase tracking-widest text-[#fa003f]">
          {phase.tag} · {phase.subtitle}
        </span>
        <h1 className="font-head text-4xl font-black uppercase tracking-tight text-white">
          {phase.name}
        </h1>
      </div>

      <div className="mt-6 space-y-6 px-5">
        {phase.weeks.map((week) => (
          <div key={week.id} data-testid={`week-${week.id}`}>
            <h2 className="font-head text-xl uppercase tracking-wide text-zinc-400 mb-2">
              {week.name}
            </h2>
            <div className="space-y-2">
              {week.days.map((day, di) => {
                if (day.type === "rest") {
                  return (
                    <div
                      key={di}
                      className="flex items-center gap-3 border border-zinc-900 bg-[#0d0d0d] px-4 py-3"
                    >
                      <span className="font-head text-sm w-9 text-zinc-600">{day.weekday}</span>
                      <Moon size={16} className="text-[#00f0ff]/60" />
                      <span className="text-sm uppercase tracking-wider text-zinc-600">
                        Riposo
                      </span>
                    </div>
                  );
                }
                const comp = dayCompletion(day, completed);
                const done = isDayComplete(day, completed);
                return (
                  <button
                    key={di}
                    data-testid={`day-row-${day.id}`}
                    onClick={() => navigate(`/session/${day.id}`)}
                    className={`w-full flex items-center gap-3 border px-4 py-3 active:scale-[0.99] transition-transform ${
                      done ? "border-[#fa003f]/50 bg-[#fa003f]/[0.06]" : "border-zinc-800 bg-[#141414]"
                    }`}
                  >
                    <span className="font-head text-sm w-9 text-zinc-500">{day.weekday}</span>
                    <div className="flex-1 text-left min-w-0">
                      <p className="font-head text-xl uppercase tracking-tight text-white leading-none">
                        {day.label}
                      </p>
                      <div className="mt-1.5 flex items-center gap-2">
                        <div className="flex-1 max-w-[120px]">
                          <ProgressBar pct={comp.pct} />
                        </div>
                        <span className="text-[11px] text-zinc-500">
                          {comp.done}/{comp.total}
                        </span>
                      </div>
                    </div>
                    {done ? (
                      <span className="flex h-7 w-7 items-center justify-center bg-[#fa003f]">
                        <Check size={16} strokeWidth={3} className="text-white" />
                      </span>
                    ) : (
                      <ChevronRight size={20} className="text-zinc-600" />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
